import type { TFunction } from 'i18next'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { CircleCloseIcon } from '@icons/CircleCloseIcon'
import { CircleSuccessLineIcon } from '@icons/CircleSuccessLineIcon'
import { ClockTimeIcon } from '@icons/ClockTimeIcon'

import { CampaignStatusEnum } from '@model/CampaignStatusEnum'
import { RebateApprovalStatus } from '@model/RebateApprovalsDto'
import { TransactionStatus } from '@model/TransactionStatus'

import { Badge, BadgeColor, BadgeSize } from './Badge'

enum StatusBadgeType {
  Black,
  Green,
  Blue,
  Red,
  Yellow,
}

interface Props {
  type: StatusBadgeType
  showIcon?: boolean
  label: string
  size?: BadgeSize
}

export const StatusBadge = (props: Props): JSX.Element => {
  const { type, label, showIcon, size = 'medium' } = props
  const { t } = useTranslation()

  const kind = {
    [StatusBadgeType.Black]: {
      icon: <CircleSuccessLineIcon color='textInverseBlackNeutral' />,
      color: 'black',
    },
    [StatusBadgeType.Green]: {
      icon: <CircleSuccessLineIcon color='textSuccessHighEm' />,
      color: 'green',
    },
    [StatusBadgeType.Blue]: { icon: <ClockTimeIcon color='textInfoHighEm' />, color: 'blue' },
    [StatusBadgeType.Red]: {
      icon: <CircleCloseIcon color='textInverseBlackNeutral' />,
      color: 'red',
    },
    [StatusBadgeType.Yellow]: {
      icon: <ClockTimeIcon color='textWarningHighEm' />,
      color: 'yellow',
    },
  }

  return (
    <Badge.New
      key={label}
      size={size}
      text={t(label)}
      leftIcon={showIcon ? () => kind[type].icon : undefined}
      color={kind[type].color as BadgeColor}
    />
  )
}

interface RebateApprovalsProps {
  status: RebateApprovalStatus
}

StatusBadge.RebateApprovals = ({ status }: RebateApprovalsProps) => {
  switch (status) {
    case RebateApprovalStatus.New:
      return <StatusBadge type={StatusBadgeType.Black} showIcon label='New' />
    case RebateApprovalStatus.Pending:
      return <StatusBadge type={StatusBadgeType.Blue} showIcon label='Pending' />
    case RebateApprovalStatus.Approved:
      return <StatusBadge type={StatusBadgeType.Green} showIcon label='Approved' />
    case RebateApprovalStatus.Rejected:
      return <StatusBadge type={StatusBadgeType.Red} showIcon label='Rejected' />
    case RebateApprovalStatus.Failed:
      return <StatusBadge type={StatusBadgeType.Red} showIcon label='Failed' />
    case RebateApprovalStatus.Reversed:
      return <StatusBadge type={StatusBadgeType.Red} showIcon label='Reversed' />
  }
}

interface TransactionsProps {
  status: TransactionStatus
}

StatusBadge.RebateTransactions = (props: TransactionsProps) => {
  switch (props.status) {
    case TransactionStatus.AwaitingApproval:
    case TransactionStatus.Pending:
      return <StatusBadge type={StatusBadgeType.Blue} showIcon label='Pending' />
    case TransactionStatus.Completed:
      return <StatusBadge type={StatusBadgeType.Green} showIcon label='Completed' />
    case TransactionStatus.Cancelled:
    case TransactionStatus.Declined:
    case TransactionStatus.Failed:
      return <StatusBadge type={StatusBadgeType.Red} showIcon label='Failed' />
    case TransactionStatus.Reversed:
      return <StatusBadge type={StatusBadgeType.Red} showIcon label='Reversed' />
  }
}

interface ContestProps {
  status: CampaignStatusEnum
}

export const getIBContestBadgeStatusLabel = (status: CampaignStatusEnum, t: TFunction): string => {
  switch (status) {
    case CampaignStatusEnum.Enabled:
      return t('Active')
    case CampaignStatusEnum.Disabled:
      return t('Ended')
    case CampaignStatusEnum.Paused:
      return t('Paused')
  }
}

StatusBadge.IBContest = (props: ContestProps) => {
  const baseProps: Partial<Props> = { size: 'medium' }

  switch (props.status) {
    case CampaignStatusEnum.Enabled:
      return <StatusBadge type={StatusBadgeType.Green} {...baseProps} label='Active' />
    case CampaignStatusEnum.Disabled:
      return <StatusBadge type={StatusBadgeType.Black} {...baseProps} label='Ended' />
    case CampaignStatusEnum.Paused:
      return <StatusBadge type={StatusBadgeType.Yellow} {...baseProps} label='Paused' />
  }
}
